import { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useGetBalance } from '@/query/queries/cash/useGetBalance';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import {
  EARNINGS_STATUSES as EARNINGS_STATUSES_T,
  EMPTY_DASH,
  LOAN_GROUPS as LOAN_GROUPS_T,
  LOAN_GROUPS_IDS,
} from '@/helpers/constants';
import { LOAN_EARNINGS_STATUSES } from '@/helpers/loan';
import { DATE_FORMAT_DD_MMM_YYYY } from '@/helpers/common';
import { useGetLoansByFundraisingId } from '@/query/queries/loan/useGetLoansByFundraisingId';
import { useGetRepaymentScheduleByFundraisingId } from '@/query/queries/loan/useGetRepaymentScheduleByFundraisingId';
import { useGetLoanEarningsByFundraisingId } from '@/query/queries/loan/useGetLoanEarningsByFundraisingId';
import { ShiftPaymentInfo } from './ShiftPaymentInfo';
import { MyTooltip } from '@/webkit/MyTooltip';
import IconShift from './icon_shift.svg';
import IconTooltip from './icon_tooltip.svg';
import s from './index.module.scss';

export const LoanEarningsSchedule = ({ group, fundraisingID }) => {
  const { t } = useTranslation('loans');
  const { t: tConstants } = useTranslation('constants');
  const EARNINGS_STATUSES = EARNINGS_STATUSES_T(tConstants);
  const LOAN_GROUPS = LOAN_GROUPS_T(tConstants);
  const [groupId, setGroupId] = useState(null);

  const { data: balance } = useGetBalance();
  const { data: loans } = useGetLoansByFundraisingId(fundraisingID);
  const { data, isLoading } = useGetLoanEarningsByFundraisingId(
    fundraisingID,
    groupId,
  );

  const { data: scheduleData } = useGetRepaymentScheduleByFundraisingId(
    fundraisingID,
    group,
  );

  const isPaid = useMemo(
    () =>
      !!(
        scheduleData &&
        scheduleData.list.filter(s => s.status === 'paid').length ===
          scheduleData.list.length
      ),
    [scheduleData],
  );

  useEffect(() => {
    if (loans?.length && !groupId) {
      setGroupId(loans[0].id);
    }
  }, [loans, groupId]);

  if (!loans?.length || !data) {
    return null;
  }

  const { total_earnings, list } = data;
  const flatFeePaid = loans.find(loan => loan.id === groupId)?.loans
    ?.flat_fee_paid;
  const earningDiff = total_earnings - flatFeePaid;

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <h2 className="f-article-accent">{t('loan_info.earnings_schedule')}</h2>
        <div className="f-small-regular">
          {t('loan_info.track_monthly_income')}
        </div>
      </div>
      <ShiftPaymentInfo banners={data?.flexibility_banners} />
      <div className={s.tabs}>
        {loans?.map(item => (
          <div
            key={item.id}
            onClick={() => setGroupId(item.id)}
            className={classnames(
              s.tabs_item,
              item.id === groupId && 'active',
              'f-article-regular',
            )}
          >
            {LOAN_GROUPS[item.id].full_name}
          </div>
        ))}
      </div>

      {!isLoading &&
        (data?.list.length ? (
          <>
            <div className={s.table__wrapper}>
              <div className={s.table__inner}>
                <div className={s.table}>
                  <div className={classnames(s.table__thead)}>
                    <div className={s.table__thead_col}>
                      {t('loan_info.due_date')}
                    </div>
                    <div className={s.table__thead_col}>
                      {t('loan_info.loan_balance')}
                    </div>
                    <div className={s.table__thead_col}>
                      {t('loan_info.earnings_percent')}
                    </div>
                    <div className={s.table__thead_col}>
                      {t('loan_info.earnings_currency')}
                    </div>
                    <div className={s.table__thead_col}>
                      {t('loan_info.status')}
                    </div>
                  </div>

                  <div className={s.table__tbody}>
                    {list.map((loan, idx) => {
                      const isLast = idx === list.length - 1;
                      return (
                        <div
                          className={classnames(
                            s.table__tbody_row,
                            loan.status,
                          )}
                          key={idx}
                        >
                          <div className={s.table__tbody_col}>
                            <span className="f-small-accent">
                              {prettyDate(
                                loan.deadline,
                                DATE_FORMAT_DD_MMM_YYYY,
                              )}
                            </span>
                          </div>
                          <div className={s.table__tbody_col}>
                            <div className={s.row}>
                              <span className="f-small-regular">
                                {prettyFloatMoney(loan.loan_balance, {
                                  decimalPlaces: 2,
                                })}
                              </span>
                              {loan.flexibility_info && (
                                <MyTooltip
                                  theme="dark"
                                  renderContent={() => (
                                    <div
                                      className={classnames(
                                        'f-small-regular',
                                        s.tooltip_content,
                                      )}
                                    >
                                      {t(
                                        loan.flexibility_info?.earnings_amount <
                                          0.01
                                          ? 'loan_info.earnings_reschedule_less_tooltip'
                                          : 'loan_info.earnings_reschedule_tooltip',
                                        {
                                          earningsAmount: prettyFloatMoney(
                                            loan.flexibility_info
                                              ?.earnings_amount,
                                            { decimalPlaces: 2 },
                                          ),
                                          brands:
                                            loan.flexibility_info?.brands.join(
                                              ', ',
                                            ),
                                        },
                                      )}
                                    </div>
                                  )}
                                  renderElement={() => (
                                    <div className={s.tooltip_icon}>
                                      <img
                                        src={IconShift}
                                        alt="shift"
                                        width={16}
                                        height={16}
                                      />
                                    </div>
                                  )}
                                />
                              )}
                            </div>
                          </div>
                          <div className={s.table__tbody_col}>
                            {!loan.earnings_percent ? (
                              EMPTY_DASH
                            ) : (
                              <>
                                {(LOAN_EARNINGS_STATUSES.accrued ===
                                  loan.status ||
                                  groupId === LOAN_GROUPS_IDS.moderate.id) && (
                                  <span className="f-small-regular">
                                    {loan.earnings_percent}%
                                  </span>
                                )}

                                {groupId === LOAN_GROUPS_IDS.conservative.id &&
                                  LOAN_EARNINGS_STATUSES.scheduled ===
                                    loan.status && (
                                    <div className={s.row}>
                                      <span className="f-small-regular">
                                        {t('loan_info.up_to')}{' '}
                                        {loan.earnings_percent}%{' '}
                                      </span>
                                      {balance && (
                                        <MyTooltip
                                          theme="dark"
                                          renderContent={() => (
                                            <div
                                              className={classnames(
                                                'f-small-regular',
                                                s.tooltip_content,
                                              )}
                                            >
                                              {balance.invested >= 10000 ? (
                                                <span>
                                                  {t(
                                                    'loan_info.investment_balance_10000',
                                                  )}
                                                </span>
                                              ) : (
                                                <span>
                                                  {t(
                                                    'loan_info.invest_minimum_100',
                                                  )}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          renderElement={() => (
                                            <div className={s.tooltip_icon}>
                                              <img
                                                src={IconTooltip}
                                                alt="tooltip"
                                                width={16}
                                                height={16}
                                              />
                                            </div>
                                          )}
                                        />
                                      )}
                                    </div>
                                  )}
                              </>
                            )}
                          </div>
                          <div className={s.table__tbody_col}>
                            <span className="f-small-regular">
                              {isPaid && earningDiff && isLast
                                ? prettyFloatMoney(
                                    loan.earnings_currency - earningDiff,
                                    { decimalPlaces: 2 },
                                  )
                                : prettyFloatMoney(loan.earnings_currency, {
                                    decimalPlaces: 2,
                                  })}
                            </span>
                          </div>
                          <div className={s.table__tbody_col}>
                            <span
                              className={classnames(
                                s.status,
                                loan.status,
                                'f-small-regular',
                              )}
                            >
                              {EARNINGS_STATUSES[loan.status]?.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className={s.total}>
              <div className={s.total_label}>
                <span className="f-body-accent">
                  {t('loan_info.total_earnings')}
                </span>
              </div>
              <div className={s.total_value}>
                <span className="f-body-accent">
                  {isPaid && earningDiff
                    ? prettyFloatMoney(flatFeePaid, { decimalPlaces: 2 })
                    : prettyFloatMoney(total_earnings, { decimalPlaces: 2 })}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className={s.empty_text}>
            <span className="f-small-regular">{t('loan_info.no_data')}</span>
          </div>
        ))}
    </div>
  );
};
